import { useCallback, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { Formik } from 'formik';
import isEqual from 'lodash/isEqual';
import { useAlert, AddressVerificationProvider, useWithAddressVerify, Button, Loader } from '@uplift/ui-v2';

import { useQueryClient } from '@tanstack/react-query';
import { useCurrentuser } from '@uplift/contexts';
import { useFeatureFlag, useL10n } from '@uplift/hooks';
import { useUserBillingQuery, useUserWithProfileQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { AddressType } from '@uplift/types';

import { WalletButton, WalletPaymentResult } from '../../../../../components';
import { useSaveBilling } from '../../../../payments/components/queries';
import {
  StepContainer, StepContent, StepForm, StepHeading, StepNavigator, StepNextButton
} from '../../../components';
import { PaymentInfoForm } from './form';
import { PaymentInfoFormType, paymentInfoValidation } from './validation';

export type PaymentInfoProps = {
  onNext: () => void;
  onBack: () => void;
  banner?: React.ReactNode;
};

type PaymentInfoContentProps = PaymentInfoProps & {
  initialValues: PaymentInfoFormType;
};

const PaymentInfoContent: React.VFC<PaymentInfoContentProps> = (props) => {
  const snackbar = useAlert();
  const l10n = useL10n();
  const currentUser = useCurrentuser();
  const queryClient = useQueryClient();
  const { data: userWithProfile } = useUserWithProfileQuery(currentUser.id);
  const withAddressVerify = useWithAddressVerify();
  const { savePaymentCard, insertBilling, saveWalletPayment } = useSaveBilling();
  const [ walletChecked, setWalletChecked ] = useState(false);
  const [ walletAvailable, setWalletAvailable ] = useState(false);
  const [ showCardForm, setShowCardForm ] = useState(false);
  const walletPaymentEnabled = useFeatureFlag('WALLET_PAYMENT_ENABLED');

  const handleWalletAvailabilityChange = useCallback((available: boolean) => {
    setWalletAvailable(available);
    setWalletChecked(true);
  }, []);

  const displayCardForm = showCardForm || !walletAvailable;

  const onWalletPayment = async ({ paymentMethodId, billingAddress }: WalletPaymentResult) => {
    try {
      const billing = await insertBilling({
        line1: billingAddress.address1,
        line2: billingAddress.address2,
        city: billingAddress.city,
        stateId: billingAddress.state,
        zip: billingAddress.zip,
      });

      if (!billing) {
        throw new Error();
      }

      const response = await saveWalletPayment({ paymentMethodId, resourceIds: [] });

      if (!response || response.errorCode) {
        snackbar.error('There was an error saving your payment method. Please try again.');
        return;
      }

      await queryClient.invalidateQueries({ queryKey: [ 'user', currentUser.id, 'billing' ] });

      props.onNext();
    } catch (err) {
      console.error(err);
    }
  };

  const onSubmit = withAddressVerify(async (values: PaymentInfoFormType) => {
    try {
      const address: AddressType = values.billingAndHomeAddressSame
        ? {
          address1: userWithProfile.profile.address.line1,
          address2: userWithProfile.profile.address.line2,
          city: userWithProfile.profile.address.city,
          state: userWithProfile.profile.address.stateId,
          zip: userWithProfile.profile.address.zip
        } : values.address;

      const billing = await insertBilling({
        line1: address.address1,
        line2: address.address2,
        city: address.city,
        stateId: address.state,
        zip: address.zip,
      });

      if (!billing) {
        throw new Error();
      }

      const [ expMonth, expYear ] = CalendarDateService.date(values.expirationDate, 'MM/YY').format('MM-YY').split('-').map(Number);

      const response = await savePaymentCard({
        cardHolderName: values.nameOnCard,
        cvc: values.cvc,
        expMonth: expMonth,
        expYear: expYear,
        number: values.cardNumber,
        resourceIds: []
      });

      if (!response || response.error || response.errorCode) {
        switch (response?.errorCode) {
          case 'expired_card':
            snackbar.error('Your card has expired. Please update your card information.');
            break;
          case 'incorrect_cvc':
            snackbar.error('Your CVC is incorrect. Please update your card information.');
            break;
          case 'incorrect_number':
            snackbar.error('Your card number is incorrect. Please update your card information.');
            break;
          default:
            snackbar.error('There was an error saving your card. Please try again.');
            break;
        }

        return;
      }

      await queryClient.invalidateQueries({ queryKey: [ 'user', currentUser.id, 'billing' ] });

      props.onNext();
    } catch (err) {
      console.error(err);
    }
  }, {
    shouldCheck: (_address, { billingAndHomeAddressSame }) => !billingAndHomeAddressSame,
    initialAddress: props.initialValues.address,
    getAddressFromFormikValues: (values: PaymentInfoFormType) => values.address,
  });

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={props.initialValues}
      validationSchema={paymentInfoValidation(l10n)}
    >
      {formik => (
        <StepContainer>
          {props.banner}
          <StepContent>
            <StepHeading>Payment</StepHeading>
            {walletPaymentEnabled ? (
              <>
                <Box sx={{ display: walletChecked && walletAvailable ? 'block' : 'none' }}>
                  <WalletButton onWalletPayment={onWalletPayment} onAvailabilityChange={handleWalletAvailabilityChange} />
                </Box>

                {!walletChecked && (
                  <Box py={3} display="flex" justifyContent="center">
                    <Loader height="120px" />
                  </Box>
                )}

                {walletChecked && walletAvailable && (
                  <Box py={2} display="flex" alignItems="center" gap={2}>
                    <Divider sx={{ flexGrow: 1 }} />
                    <Typography variant="body2" color="textSecondary">
                      {showCardForm ? l10n.accountSettings.billing.orEnterCardDetails : 'OR'}
                    </Typography>
                    <Divider sx={{ flexGrow: 1 }} />
                  </Box>
                )}

                {walletChecked && walletAvailable && !showCardForm && (
                  <Button variant="outlined" color="secondary" fullWidth onClick={() => setShowCardForm(true)}>
                    {l10n.accountSettings.billing.addPaymentMethod}
                  </Button>
                )}

                {walletChecked && displayCardForm && (
                  <StepForm onSubmit={formik.handleSubmit}>
                    <PaymentInfoForm />
                  </StepForm>
                )}
              </>
            ) : (
              <StepForm onSubmit={formik.handleSubmit}>
                <PaymentInfoForm />
              </StepForm>
            )}
          </StepContent>
          <StepNavigator onBack={props.onBack}>
            <StepNextButton disabled={!formik.isValid || formik.isSubmitting} />
          </StepNavigator>
        </StepContainer>
      )}
    </Formik>
  );
};

export const PaymentInfo: React.VFC<PaymentInfoProps> = props => {
  const currentUser = useCurrentuser();
  const { data: userWithProfile, isLoading: isUserWithProfileLoading } = useUserWithProfileQuery(currentUser.id);
  const { data: userBilling, isLoading: isUserBillingLoading } = useUserBillingQuery(currentUser.id);
  const isLoading = isUserWithProfileLoading || isUserBillingLoading;

  if (isLoading) {
    return null;
  }

  const billingAddress: AddressType = {
    address1: userBilling?.billing?.address?.line1 ?? '',
    address2: userBilling?.billing?.address?.line2 ?? '',
    city: userBilling?.billing?.address?.city ?? '',
    state: userBilling?.billing?.address?.stateId ?? '',
    zip: userBilling?.billing?.address?.zip ?? ''
  };

  const userProfileAddress: AddressType = {
    address1: userWithProfile?.profile?.address?.line1 ?? '',
    address2: userWithProfile?.profile?.address?.line2 ?? '',
    city: userWithProfile?.profile?.address?.city ?? '',
    state: userWithProfile?.profile?.address?.stateId ?? '',
    zip: userWithProfile?.profile?.address?.zip ?? ''
  };

  /**
   * By default, the "My billing and home address are the same" input should be Yes
   * if the billing address exists, and is different from the user's home address, we check the equality to determine the default value
   */
  const addressIsEqual = isEqual(billingAddress, userProfileAddress);
  const hasAddress = !!billingAddress.address1;
  const billingAndHomeAddressSame = hasAddress ? addressIsEqual : true;

  const initialValues: PaymentInfoFormType = {
    nameOnCard: userBilling?.billingDetails?.name ?? '',
    cardNumber: '',
    expirationDate: userBilling?.expiration ? userBilling?.expiration?.format?.('MM/YY') : '',
    cvc: '',
    billingAndHomeAddressSame: billingAndHomeAddressSame,
    address: billingAndHomeAddressSame ? {
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: ''
    } : billingAddress,
  };

  return (
    <AddressVerificationProvider
      shouldReverifyAddress={false}
      parsedVerifiedAddress={initialValues.address.address1 ? initialValues.address : null}
    >
      <PaymentInfoContent {...props} initialValues={initialValues} />
    </AddressVerificationProvider>
  );
};
